import {useCallback, useMemo} from 'react'
import {createRootRoute, Outlet, useNavigate} from '@tanstack/react-router'
import {ClerkProvider} from '@clerk/clerk-react'
import {ErrorBoundary} from 'react-error-boundary'
import {PostHogProvider} from 'posthog-js/react'
import posthog from 'posthog-js'
import {HelmetProvider} from 'react-helmet-async'
import {QueryClientProvider} from '@tanstack/react-query'
import {ReactQueryDevtools} from '@tanstack/react-query-devtools'
import InputMask from '@/components/common/InputMask'
import Announcement from '@/components/common/Announcement/Announcement'
import {FallbackError} from '@/components/FallbackError'
import ScreenProvider from '@/providers/ScreenProvider'
import {AnnouncementsProvider, useAnnouncementsContext} from '@/contexts/AnnouncementsContext'
import {RiveLoaderProvider} from '@/contexts/RiveLoaderContext'
import useGlobalError from '@/hooks/useGlobalError'
import useDevice from '@/hooks/useDevice'
import useInternetConnection from '@/hooks/useInternetConnection'
import {useFeatureFlags} from '@/hooks/useFeatureFlags'
import {usePosthogIdentify} from '@/utils/posthog'
import {useSaveLastUrl} from '@/hooks/useRedirectToLastUrl'
import {queryParams} from '@/helpers'
import {queryClient} from '@/utils/queryClient'
import {ErrorComponent} from '@/components/error'
import {APP_VERSION, CLERK_PUBLISHABLE_KEY, IS_DEV, IS_STAGING, NODE_ENV, POSTHOG_HOST, POSTHOG_KEY} from "@/constants";

// PostHog — initialized once at module load (not inside React render)
if (POSTHOG_KEY) {
	posthog.init(POSTHOG_KEY, {
		api_host: POSTHOG_HOST,
		ui_host: 'https://eu.i.posthog.com',
		person_profiles: 'always',
		// Ship logs to PostHog Logs (replaces Datadog log shipping)
		logs: {
			serviceName: `frontend${IS_STAGING ? '-staging' : ''}`,
			environment: NODE_ENV,
			serviceVersion: APP_VERSION,
		},
		loaded: (ph) => {
			if (IS_DEV) ph.debug()
		},
	})
}

// RootLayout renders all providers. Query/context hooks are NOT called here —
// they go in AppShell which renders inside the providers.
const RootLayout = () => {
	const navigate = useNavigate()

	// App initialization (token from native app URL params)
	// Note: getUser() thunk removed — AuthIntermediator handles user fetching via TanStack Query
	useCallback(() => {
		const params = queryParams()
		const isApp = params.get('source') === 'app'
		localStorage.setItem('isApp', isApp.toString())
	}, [])

	// Clerk needs router callbacks for its internal navigation (SSO redirects, etc.)
	// Normalize path: Clerk sometimes passes relative paths without leading /
	const routerPush = useCallback((to: string) => {
		void navigate({to: to.startsWith('/') ? to : `/${to}`})
	}, [navigate])

	const routerReplace = useCallback((to: string) => {
		void navigate({to: to.startsWith('/') ? to : `/${to}`, replace: true})
	}, [navigate])

	return (
		<HelmetProvider>
			<PostHogProvider client={posthog}>
				<ErrorBoundary
					fallbackRender={FallbackError}
					onReset={() => {
						localStorage.clear();
						sessionStorage.clear();
						location.reload()
					}}
				>
					<ClerkProvider
						routerPush={routerPush}
						routerReplace={routerReplace}
						publishableKey={CLERK_PUBLISHABLE_KEY}
						clerkJSUrl="https://unpkg.com/@clerk/clerk-js@5.52.1/dist/clerk.browser.js"
					>
						<QueryClientProvider client={queryClient}>
							<RiveLoaderProvider>
								<AnnouncementsProvider>
									<ScreenProvider>
										<AppShell/>
									</ScreenProvider>
								</AnnouncementsProvider>
							</RiveLoaderProvider>
							{IS_DEV && <ReactQueryDevtools initialIsOpen={false}/>}
						</QueryClientProvider>
					</ClerkProvider>
				</ErrorBoundary>
			</PostHogProvider>
		</HelmetProvider>
	)
}

// AppShell renders inside providers so all hooks work here
const AppShell = () => {
	useDevice()
	useInternetConnection()
	useFeatureFlags()
	usePosthogIdentify()
	useSaveLastUrl()

	const [error] = useGlobalError()
	const {announcements} = useAnnouncementsContext()
	const errorAnnouncement = useMemo(() => announcements.find((a: any) => !a.modal), [announcements])
	const modalAnnouncements = useMemo(() => announcements.filter((a: any) => a.modal), [announcements])

	return (
		<div className="h-dvh">
			{error || errorAnnouncement ? (
				<ErrorComponent err={error} {...(errorAnnouncement as any || {})} />
			) : (
				<>
					<Outlet/>
					{modalAnnouncements.map((announcement: any) => (
						<Announcement key={announcement.id} {...announcement} />
					))}
				</>
			)}
			<InputMask/>
		</div>
	)
}

export const Route = createRootRoute({
	component: RootLayout,
	notFoundComponent: () => <ErrorComponent statusCode={404}/>,
})
